import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  from,
  fromEvent,
  merge,
  of,
} from 'rxjs';
import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';
import { GetVoipVm } from '../models';
import { WebSocketInterface, UA } from 'jssip';
import { CoreService, UserProfileService } from '@app/core/services';
import {
  RTCSession,
  EndEvent,
  IncomingEvent,
  PeerConnectionEvent,
  IceCandidateEvent,
  ConnectingEvent,
  SendingEvent,
  RTCSessionEventMap,
  HoldEvent,
  IncomingAckEvent,
  IncomingDTMFEvent,
  IncomingInfoEvent,
  MediaConstraints,
  OutgoingAckEvent,
  OutgoingDTMFEvent,
  OutgoingEvent,
  OutgoingInfoEvent,
  ReInviteEvent,
  ReferEvent,
  SDPEvent,
} from 'jssip/lib/RTCSession';
import {
  IncomingRTCSessionEvent,
  ConnectedEvent,
  RegisteredEvent,
  UAConfiguration,
  UAEventMap,
  UAConnectingEvent,
  UnRegisteredEvent,
  OutgoingRTCSessionEvent,
  IncomingMessageEvent,
  OutgoingMessageEvent,
} from 'jssip/lib/UA';
import { DisconnectEvent } from 'jssip/lib/WebSocketInterface';

import {
  AuthService,
  LoggerService,
  PlatformService,
  shareReplayRefCount,
  Unity4Claim,
} from '@adreno-common';
import {
  take,
  map,
  shareReplay,
  filter,
  tap,
  switchMap,
  startWith,
  withLatestFrom,
  delay,
  debounceTime,
  distinctUntilChanged,
  takeWhile,
  takeUntil,
} from 'rxjs/operators';
import { TeleworkerState } from '@app/core/models';
import { IncomingRequest } from 'jssip/lib/SIPMessage';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class SIPService {
  private acquiredMic$ = new BehaviorSubject<undefined | MediaStream | 'none'>(
    undefined
  );
  private micStream$!: Observable<
    undefined | MediaStream | 'access' | 'none' | 'lost'
  >;
  private nailInCall$!: Observable<RTCSession>;
  private nailInCallEvents$!: Observable<CallEvent>;
  private phone$!: Observable<UA>;
  private phoneEvent$!: Observable<UAEvent>;
  private startRegistration$ = new Subject();
  private stopRegistration$ = new Subject();
  private userVoipApi: string = `${environment.apiPath}/users/voip`;

  deviceLost$!: Observable<'access' | 'none' | 'lost'>;
  nailInCallLost$!: Observable<
    | 'unregistered'
    | 'registrationFailed'
    | 'callFailed'
    | 'callEnded'
    | undefined
  >;
  nailedIn$!: Observable<boolean>;
  registered$!: Observable<boolean>;
  speakerStream$!: Observable<MediaStream | undefined>;
  status$!: Observable<{ error: boolean; msg: string }>;
  voip$!: Observable<boolean>;

  constructor(
    private userProfileSvc: UserProfileService,
    private http: HttpClient,
    private coreSvc: CoreService,
    private logger: LoggerService,
    private platform: PlatformService,
    private authSvc: AuthService,
    private toastr: ToastrService
  ) {
    this.voip$ = this.http.get<GetVoipVm>(this.userVoipApi).pipe(
      map((vm) => vm.voip),
      shareReplayRefCount()
    );
    this.initMicStream();
    this.initWebRTCPhone();
  }

  private acquireMic() {
    return this.listDevices('audioinput').pipe(
      switchMap((devices) => {
        this.logger.info('SIPService', 'micStream$.devices', devices)();
        if (devices.length == 0) {
          return of('none' as const);
        } else {
          const os = this.platform.getOS();
          const browser = this.platform.getBrowser();
          const constraints = {
            video: false,
            audio:
              os == 'windows' && (browser == 'edge' || browser == 'chrome')
                ? { deviceId: 'communications' }
                : true,
          } as MediaStreamConstraints;
          return from(navigator.mediaDevices.getUserMedia(constraints)).pipe(
            map((stream) => (!stream || !stream.active ? 'none' : stream))
          );
        }
      }),
      tap((stream) => this.acquiredMic$.next(stream))
    );
  }

  private forefitMic() {
    this.micStream$.pipe(take(1)).subscribe((stream) => {
      if (typeof stream == 'object' && stream.active) {
        this.logger.info('SIPService', 'forefitMic', {
          stream: stream.getTracks()[0].label,
          active: stream.active,
        })();
        stream.getTracks().forEach((track) => track.stop());
      }
    });
  }

  private initMicStream() {
    this.voip$
      .pipe(
        filter((voip) => !!voip),
        switchMap(() => this.listDevices('audioinput')),
        take(1)
      )
      .subscribe((devices) => {
        this.logger.info('SIPService', 'listDevices', devices)();
        if (devices.length == 0) {
          this.toastr.error(
            'No audio input devices found. Please check your audio settings.',
            'Audio Input Error',
            {
              disableTimeOut: true,
              closeButton: true,
            }
          );
        }
      });

    this.micStream$ = this.voip$.pipe(
      filter((voip) => !!voip),
      switchMap(() => this.acquiredMic$),
      filter((stream) => !!stream),
      tap((stream) => {
        if (typeof stream == 'string') {
          this.logger.info('SIPService', 'micStream$.stream', { stream })();
        }
      }),
      switchMap((stream) => {
        return fromEvent<Event>(navigator.mediaDevices, 'devicechange').pipe(
          startWith(undefined),
          switchMap((dcEvent) =>
            !!dcEvent
              ? this.listDevices('audioinput').pipe(
                  map((devices) => [dcEvent, devices] as const)
                )
              : of([dcEvent, undefined] as const)
          ),
          tap(([dcEvent, devices]) => {
            if (!!dcEvent) {
              this.logger.warn('SIPService', 'micStream$.deviceChange', {
                type: dcEvent.type,
                stream:
                  typeof stream == 'string'
                    ? stream
                    : {
                        label: stream?.getTracks()[0]?.label,
                        active: stream?.active,
                      },
                devices,
              })();
            }
          }),
          map((dcEvent) => {
            if (
              !!dcEvent &&
              typeof stream != 'string' &&
              (!stream || !stream.active)
            ) {
              stream!.getTracks().forEach((track) => track.stop());
              return 'lost' as const;
            }
            return stream;
          }),
          takeWhile((stream) => stream != undefined && stream != 'lost', true)
        );
      }),
      shareReplayRefCount()
    );

    this.deviceLost$ = this.micStream$.pipe(
      filter((stream) => stream !== undefined && typeof stream != 'object'),
      map((stream) => stream! as 'access' | 'none' | 'lost')
    );
  }

  private initWebRTCPhone() {
    this.phone$ = this.voip$.pipe(
      filter((voip) => !!voip),
      take(1),
      withLatestFrom(
        this.userProfileSvc.userProfile$.pipe(
          filter((userProfile) => !!userProfile)
        ),
        this.authSvc.hasClaim$(Unity4Claim.UseUkVOIPFromSiddle)
      ),
      map(([_, { userId, tenantId }, useUk]) => {
        const wssurl =
          tenantId == 60
            ? 'wss-uk.unity4.com'
            : tenantId == 120
            ? 'wss-us.unity4.com'
            : useUk
            ? 'wss-uk.unity4.com'
            : 'wss.unity4.com';
        var socket = new WebSocketInterface('wss://' + wssurl + '/ws');
        const configuration = {
          display_name: userId.toString(),
          uri: 'sip:' + userId + '@wss.unity4.com',
          password: 'lboles1cuqczxqmhjewmdfn1',
          sockets: [socket],
          registrar_server: undefined,
          contact_uri: undefined,
          authorization_user: userId.toString(),
          instance_id: undefined,
          session_timers: true,
          use_preloaded_route: false,
        } as UAConfiguration;
        return new UA(configuration);
      }),
      shareReplayRefCount()
    );

    this.phoneEvent$ = this.phone$.pipe(
      switchMap((p) => merge(...uaEvents.map((e) => uaBinder(p, e)))),
      shareReplayRefCount()
    );

    this.phoneEvent$.subscribe({
      next: (pe) =>
        this.logger.info('SIPService', 'phoneEvent', { type: pe.type }),
      error: (err) => this.logger.error('SIPService', 'phoneEvent', { err }),
      complete: () => this.logger.debug('SIPService', 'phoneEvent', 'complete'),
    });

    this.registered$ = this.phoneEvent$.pipe(
      switchMap(() => this.phone$),
      map((phone) => phone.isConnected() && phone.isRegistered()),
      startWith(false)
    );

    this.nailInCall$ = this.phoneEvent$.pipe(
      filter((pe) => pe.type == 'newRTCSession'),
      map(
        (pe) => pe.event as IncomingRTCSessionEvent | OutgoingRTCSessionEvent
      ),
      filter((rse) => rse.originator == 'remote'),
      map((e) => e as IncomingRTCSessionEvent),
      map((irse) => irse.session),
      shareReplayRefCount()
    );

    this.nailInCallEvents$ = this.nailInCall$.pipe(
      switchMap((rs) =>
        merge(...rtcSessionEvents.map((e) => rtcSessionBinder(rs, e)))
      ),
      shareReplayRefCount()
    );

    this.nailInCallEvents$.subscribe((nice) =>
      this.logger.info('SIPService', 'nailInCallEvent', { type: nice.type })
    );

    let peerConnection$ = this.nailInCallEvents$.pipe(
      tap((nice) =>
        this.logger.debug('SIPService', 'peerConnection$', { type: nice.type })
      ),
      filter((nice) => nice.type == 'peerconnection'),
      map((nice) => nice.event as PeerConnectionEvent),
      map((pce) => pce.peerconnection as RTCPeerConnection)
    );

    this.speakerStream$ = peerConnection$.pipe(
      switchMap((pc) => fromEvent<RTCTrackEvent>(pc, 'track')),
      map((rte) => rte.streams[0])
    );

    let peerConnectionStateChange$ = peerConnection$.pipe(
      switchMap((pc) =>
        fromEvent<Event>(pc, 'connectionstatechange').pipe(
          map((e) => ({ pc, e }))
        )
      )
    );

    this.nailedIn$ = combineLatest([
      this.nailInCallEvents$,
      peerConnectionStateChange$,
    ]).pipe(
      switchMap(([nice, pcsc]) =>
        this.nailInCall$.pipe(
          map((rtcSession) => [nice, pcsc, rtcSession] as const)
        )
      ),
      map(([nice, pcsc, rtcSession]) => {
        this.logger.debug('SIPService', 'nailedIn$', {
          rtcSession: rtcSession.id,
          type: nice.type,
          connectionState: pcsc.pc.connectionState,
        })();

        if (nice.type == 'ended' || nice.type == 'failed') return false;
        if (
          pcsc.pc.connectionState == 'disconnected' ||
          pcsc.pc.connectionState == 'failed'
        )
          return false;
        if (!rtcSession) return false;
        return !rtcSession.isEnded();
      }),
      distinctUntilChanged(),
      shareReplayRefCount()
    );

    this.nailInCallLost$ = merge(
      this.phoneEvent$.pipe(
        filter((pe) => pe.type == 'unregistered'),
        map((pe) => 'unregistered' as const)
      ),
      this.nailInCallEvents$.pipe(
        filter((nice) => nice.type == 'failed' || nice.type == 'ended'),
        map((nice) =>
          nice.type == 'ended'
            ? ('callEnded' as const)
            : ('callFailed' as const)
        )
      )
    ).pipe(startWith(undefined), shareReplayRefCount());

    this.status$ = merge(
      this.micStream$.pipe(
        tap((ms) =>
          this.logger.debug('SIPService', 'micStream$', {
            ms: !ms || typeof ms == 'string' ? ms : ms.getTracks()[0].label,
          })()
        ),
        map((ms) => {
          if (ms === undefined) return { msg: 'Accessing Mic' };
          if (ms == 'access') return { msg: 'Denied Mic', error: true };
          if (ms == 'none') return { msg: 'No Mic Avail', error: true };
          if (ms == 'lost') return { msg: 'Lost Mic', error: true };
          return { msg: 'Mic Acquired' };
        })
      ),
      this.phoneEvent$.pipe(
        tap((pe) =>
          this.logger.debug('SIPService', 'phoneEvent$', { type: pe.type })()
        ),
        map((pe) => {
          if (pe.type == 'connecting') return { msg: 'VOIP Connecting' };
          if (pe.type == 'connected') return { msg: 'VOIP Connected' };
          if (pe.type == 'disconnected') return { msg: 'VOIP Disconnected' };
          if (pe.type == 'registered') return { msg: 'Awaiting Nail In' };
          if (pe.type == 'unregistered')
            return { msg: 'VOIP Unregistered', error: true };
          if (pe.type == 'registrationFailed')
            return { msg: 'Retrying VOIP Registration', error: true };
          if (
            pe.type === 'newRTCSession' &&
            (pe.event as IncomingRTCSessionEvent | OutgoingRTCSessionEvent)
              .originator == 'remote'
          )
            return { msg: 'NailIn Call Ringing' };
          return { msg: pe.type };
        })
      ),
      this.nailInCallEvents$.pipe(
        tap((nice) =>
          this.logger.debug('SIPService', 'nailInCallEvents$', {
            type: nice.type,
          })()
        ),
        map((nice) => {
          if (nice.type == 'accepted') return { msg: 'NailIn Call Accepted' };
          if (nice.type == 'connecting')
            return { msg: 'NailIn Call Connecting' };
          if (nice.type === 'icecandidate') return { msg: 'STUN Handshake' };
          if (nice.type == 'confirmed') return { msg: 'Nailed In' };
          if (nice.type == 'ended') return { msg: 'NailIn Call Ended' };
          if (nice.type == 'failed')
            return { msg: 'NailIn Call Failed', error: true };
          if (nice.type == 'getusermediafailed')
            return { msg: 'Get User Media Failed', error: true };
          if (nice.type == 'peerconnection') return { msg: 'Connecting audio' };
          if (nice.type == 'sdp') return { msg: undefined }; //suppress sdp - sometimes happens after 'confirmed'
          if (nice.type == 'newDTMF') return { msg: undefined }; //suppress newDTMF - agent just used dailpad
          return { msg: nice.type };
        })
      ),
      peerConnectionStateChange$.pipe(
        tap((pcsc) =>
          this.logger.debug('SIPService', 'peerConnectionStateChange$', {
            type: pcsc.e.type,
            connectionState: pcsc.pc.connectionState,
          })()
        ),
        map((pcsc) => {
          if (pcsc.pc.connectionState == 'failed')
            return { msg: 'Connection Failed', error: true };
          if (pcsc.pc.connectionState == 'connected') return { msg: undefined };
          return { msg: pcsc.e.type };
        })
      )
    ).pipe(
      //tap((s) => this.logger.debug('SIPService', 'status$', s)()),
      filter((s) => !!s.msg),
      map((s) => ({ msg: s.msg!, error: !!s.error }))
    );

    //effects
    this.nailInCall$
      .pipe(
        switchMap((rtcSession) => {
          this.logger.info('SIPService', 'nailInCall$ acquireMic')();

          return this.acquireMic().pipe(
            filter((stream) => !!stream && stream != 'none'),
            take(1),
            map((stream) => ({ rtcSession, stream: stream as MediaStream }))
          );
        })
      )
      .subscribe(({ rtcSession, stream }) => {
        this.logger.info('SIPService', 'nailInCall$.answer', {
          stream: { label: stream.getTracks()[0].label, active: stream.active },
        })();

        //old code issued hangup on existing rtcSession (if exists) - will need pairwise here if needed
        //if (prevRctSession) prevRctSession.terminate();

        rtcSession.answer({
          //mediaConstraints: { audio: true, video: false },
          mediaStream: stream,
          pcConfig: {
            iceServers: [
              {
                urls: ['stun:stun.l.google.com:19302'],
              },
            ],
          },
        });
      });

    this.nailedIn$
      .pipe(filter((nailedIn) => !!nailedIn))
      .subscribe((nailedIn) => {
        this.logger.debug('SIPService', 'nailedIn$.subscribe', { nailedIn })();
        this.coreSvc.state = TeleworkerState.SIPNailIn;
      });

    this.nailInCallEvents$
      .pipe(
        filter((nice) => nice.type == 'icecandidate'),
        map((nice) => nice.event as IceCandidateEvent),
        //1.5 seconds timeout after the last icecandidate received!
        debounceTime(1500)
      )
      .subscribe((ice) => {
        this.logger.debug('SIPService', 'icecandidate.ready()', ice)();
        ice.ready();
      });

    this.nailInCallEvents$
      .pipe(
        filter(
          (nice) =>
            nice.type == 'failed' ||
            nice.type == 'ended' ||
            nice.type == 'getusermediafailed'
        )
      )
      .subscribe(() => {
        this.forefitMic();
      });

    merge(
      this.startRegistration$.pipe(map(() => 'start' as const)),
      this.stopRegistration$.pipe(map(() => 'stop' as const))
    )
      .pipe(
        switchMap((cmd) =>
          this.phone$.pipe(map((phone) => [cmd, phone] as const))
        ),
        tap(([cmd, phone]) => {
          this.logger.info('SIPService', 'phoneInit', {
            cmd,
            connected: phone.isConnected(),
            registered: phone.isRegistered(),
          })();

          if (cmd == 'start') {
            if (!phone.isConnected()) {
              phone.start();
              return;
            }
            if (!phone.isRegistered()) {
              phone.register();
              return;
            }
          } else if (cmd == 'stop') {
            if (phone.isRegistered()) phone.unregister();
            if (phone.isConnected()) phone.stop();
          }
        })
      )
      .subscribe({
        next: (success) =>
          this.logger.info('SIPService', 'phoneInit', 'phoneRegistered')(),
        error: (error) => {
          this.logger.error('SIPService', 'phoneInit', {
            message: error.message,
          })();
        },
        complete: () => {
          this.logger.debug('SIPService', 'phoneInit', 'complete')();
        },
      });

    this.phoneEvent$
      .pipe(
        filter((pe) => pe.type == 'registrationFailed'),
        tap((event) =>
          this.logger.info('SIPService', 'registrationRetry in 15s', event)()
        ),
        delay(15000),
        takeUntil(this.stopRegistration$),
        takeWhile((event, idx) => idx < 3, true)
      )
      .subscribe({
        next: () => {
          this.logger.info('SIPService', 'registrationRetry')();
          this.startRegistration$.next();
        },
        complete: () => {
          this.voip$.pipe(take(1)).subscribe((voip) => {
            if (!!voip) {
              this.logger.error(
                'SIPService',
                'registrationRetry',
                'VOIP Registration Failed'
              )();
              this.toastr.error('VOIP Registration Failed', 'VOIP Error', {
                disableTimeOut: true,
                closeButton: true,
              });
              this.coreSvc.state = TeleworkerState.Work;
            }
          });
        },
      });
  }

  connectPhone() {
    this.startRegistration$.next();
  }

  disconnectPhone() {
    this.stopRegistration$.next();
  }

  listDevices(kind: string) {
    return from(navigator.mediaDevices.enumerateDevices()).pipe(
      map((devices) => {
        return devices
          .map((device) => {
            const [kind, type, direction] =
              device.kind.match(deviceDetailRegex) || [];
            return {
              deviceId: device.deviceId,
              label: device.label,
              kind,
              type,
              direction,
            };
          })
          .filter((device) => device.type == 'audio' && device.kind == kind)
          .sort((a, b) => {
            if (a.deviceId == 'communications') return -1;
            if (b.deviceId == 'communications') return 1;
            if (a.deviceId == 'default') return -1;
            if (b.deviceId == 'default') return 1;
            return a.deviceId.localeCompare(b.deviceId);
          });
      })
    );
  }

  sendDTMF(key: string) {
    this.nailInCall$.pipe(take(1)).subscribe((rs) => rs.sendDTMF(key));
  }
}

type UAEvent = { event: UAEvents; type: keyof UAEventMap };
type AnyEvent = { [key: string]: any };
type UAEvents =
  | UAConnectingEvent
  | ConnectedEvent
  | DisconnectEvent
  | RegisteredEvent
  | UnRegisteredEvent
  | IncomingRTCSessionEvent
  | OutgoingRTCSessionEvent
  | IncomingMessageEvent
  | OutgoingMessageEvent
  | { event: any; request: IncomingRequest }
  | AnyEvent;
const uaEvents: (keyof UAEventMap)[] = [
  'connecting',
  'connected',
  'disconnected',
  'registered',
  'unregistered',
  'registrationFailed',
  //'registrationExpiring', //dont listen to this event - other auto re-registration will not occur
  'newRTCSession',
  'newMessage',
  'sipEvent',
];

type CallEvent = { event: RTCSessionEvents; type: keyof RTCSessionEventMap };
type RTCSessionEvents =
  | PeerConnectionEvent
  | ConnectingEvent
  | SendingEvent
  | IncomingEvent
  | OutgoingEvent
  | IncomingAckEvent
  | OutgoingAckEvent
  | EndEvent
  | IncomingDTMFEvent
  | OutgoingDTMFEvent
  | IncomingInfoEvent
  | OutgoingInfoEvent
  | HoldEvent
  | MediaConstraints
  | ReInviteEvent
  | ReferEvent
  | SDPEvent
  | IceCandidateEvent;
const rtcSessionEvents: (keyof RTCSessionEventMap)[] = [
  'peerconnection',
  'connecting',
  'sending',
  'progress',
  'accepted',
  'confirmed',
  'ended',
  'failed',
  'newDTMF',
  'newInfo',
  'hold',
  'unhold',
  'muted',
  'unmuted',
  'reinvite',
  'update',
  'refer',
  'replaces',
  'sdp',
  'icecandidate',
  'getusermediafailed',
  'peerconnection:createofferfailed',
  'peerconnection:createanswerfailed',
  'peerconnection:setlocaldescriptionfailed',
  'peerconnection:setremotedescriptionfailed',
];

function uaBinder<T extends keyof UAEventMap>(phone: UA, type: T) {
  return fromEvent<UAEvents>(phone, type as string).pipe(
    map((event) => ({ type, event } as UAEvent))
  );
  //type uaEvent = Parameters<UAEventMap[T]>[0];
  //const boundMethod = bindCallback<typeof type, uaEvent>(phone.on);
  //return boundMethod.call(phone, type).pipe(map((event) => ({ type, event: event as UAEvents } as UAEvent)));
}

function rtcSessionBinder<T extends keyof RTCSessionEventMap>(
  rtcSession: RTCSession,
  type: T
) {
  return fromEvent<RTCSessionEvents>(rtcSession, type as string).pipe(
    map((event) => ({ type, event } as CallEvent))
  );
  //type event = Parameters<RTCSessionEventMap[T]>[0];
  //const boundMethod = bindCallback<typeof type, event>(rtcSession.on);
  //return boundMethod.call(rtcSession, type).pipe(map((event) => ({ type, event: event as RTCSessionEvents } as CallEvent)));
}

const deviceDetailRegex = /(\w+)(input|output)/i;
